// libs
import * as React from 'react'
import { connect } from 'react-redux'
import { Redirect, Route, RouteComponentProps, RouteProps } from 'react-router-dom'

// classes / components
import LocationHandler from 'src/containers/location/location_handler/location_handler'
import LocationSlugHandler from 'src/containers/location/location_slug_handler/location_slug_handler'
import { LoginPageWithHeader } from 'src/pages/login'

// helpers
import { usePageTracking } from 'src/pages/hooks/hooks'

// interfaces / constants
import { navigationFeatureFlags } from 'src/components/feature_flag/navigation_feature_flag/constants'
import { NavigationFeatureFlags } from 'src/components/feature_flag/navigation_feature_flag/model'
import { FRONTEND_CREATE_PROFILE, INTENT_CREATE_PUBLIC_PROFILE } from 'src/constants/urls'
import { IRootState } from 'src/reducers/interface'
import { isIntermediateProfile } from 'src/utils/profile'
import { RouteFeatureFlag } from '../components/feature_flag/route_feature_flag/route_feature_flag'
import { AuthModalType } from '../interfaces/app-state'

interface LogoutProps {
  layout?: AuthModalType;
}

interface IProtectedPropsFromState {
  loggedIn: boolean;
  hasToken?: boolean;
  intermediateProfile?: boolean;
}

type MainRouteProps = RouteProps & {
  active?: boolean;
  layout?: AuthModalType;
}

const RedirectHome = () => {
  usePageTracking()
  return <Redirect to='/' />
}

const mapStateToPropsProtected = ({ user }: IRootState): IProtectedPropsFromState => ({
  hasToken: user.hasToken,
  loggedIn: user.loggedIn,
  intermediateProfile: isIntermediateProfile(user.profiles[user.selectedProfileId]?.type)
})

type ProtectedRouteProps = IProtectedPropsFromState & MainRouteProps & LogoutProps;

// only exported for testing use TopLevelRoutes for external use
export const LoginRequired =
  ({ active, loggedIn, path, exact, component }: ProtectedRouteProps) => (
    <TopLevelRoute
      path={path}
      exact={exact}
      active={active}
      component={loggedIn ? component : LoginPageWithHeader}
    />
  )

// only exported for testing use TopLevelRoutes for external use
export const LogoutRequired =
  ({ loggedIn, path, exact, component, layout }: ProtectedRouteProps) => (
    <TopLevelRoute
      path={path}
      exact={exact}
      component={loggedIn ? RedirectHome : component}
      layout={layout}
    />
  )

const RedirectToPublicProfileCreation = () => {
  usePageTracking()
  return <Redirect to={`${FRONTEND_CREATE_PROFILE}/profile`} />
}

// Allows logged-out users and logged-in users with intermediate profiles
export const RegistrationAllowed =
  ({ loggedIn, intermediateProfile, path, exact, component, location }: ProtectedRouteProps) => {
    const wantsPublicProfile =
      new URLSearchParams(location?.search ?? '').get('intent') === INTENT_CREATE_PUBLIC_PROFILE
    const canReachCreateProfile = !!navigationFeatureFlags.get(NavigationFeatureFlags.CREATE_PROFILE)
    const registrationDone = loggedIn && !intermediateProfile

    const redirect = wantsPublicProfile && canReachCreateProfile
      ? RedirectToPublicProfileCreation
      : RedirectHome

    return (
      <TopLevelRoute
        path={path}
        exact={exact}
        component={registrationDone ? redirect : component}
      />
    )
  }
// default Route component that sets location only use in src/routes.tsx
export const TopLevelRoute: React.FunctionComponent<MainRouteProps> =
  ({ active, path, exact, component: Component, render, layout }) => {
    const wrappedRender = (routeProps: RouteComponentProps<{ locationSlug?: string }>) => {
      const componentProps = { ...routeProps, layout }
      if (routeProps.match.params.locationSlug) {
        return (
          <LocationSlugHandler>
            {render && render(componentProps)}
            {Component && <Component {...componentProps} />}
          </LocationSlugHandler>
        )
      }
      return (
        <LocationHandler>
          {render && render(componentProps)}
          {Component && <Component {...componentProps} />}
        </LocationHandler>
      )
    }
    return (
      <RouteFeatureFlag active={active}>
        <Route
          path={path}
          exact={exact}
          render={wrappedRender}
        />
      </RouteFeatureFlag>)
  }

// lets the content(post_details/profile_feed) set the appLocation when loggedOut
export const ContentLocationRoute =
  ({ active, hasToken, path, exact, render, component: Component }: ProtectedRouteProps) => {
    const wrappedRender = (routeProps: RouteComponentProps<{ locationSlug?: string }>) => {
      const sharedElement = (
        <>
          {render && render(routeProps)}
          {Component && <Component {...routeProps} />}
        </>)
      if (hasToken) {
        return (
          <LocationHandler>
            {sharedElement}
          </LocationHandler>
        )
      }
      return sharedElement
    }
    return (
      <RouteFeatureFlag active={active}>
        <Route
          path={path}
          exact={exact}
          render={wrappedRender}
        />
      </RouteFeatureFlag>)
  }

export const ContentLocationTopLevelRoute =
  connect<IProtectedPropsFromState, object, RouteProps>(mapStateToPropsProtected)(ContentLocationRoute)

export const LoginRequiredRoute =
  connect<IProtectedPropsFromState, object, RouteProps>(mapStateToPropsProtected)(LoginRequired)

export const LogoutRequiredRoute =
  connect<IProtectedPropsFromState, object, RouteProps>(mapStateToPropsProtected)(LogoutRequired)

export const RegistrationRoute =
  connect<IProtectedPropsFromState, object, RouteProps>(mapStateToPropsProtected)(RegistrationAllowed)
